import React, { useState, useEffect, useRef } from 'react';
import VolNav from '../../components/vol/VolNav';
import { useVolunteerAuth } from '../../hooks/useVolunteerAuth';
import {
  getLocations, getShiftTimes, getPortalSettings,
  subscribeToAssignments, selfSignUpForShift, selfCancelShift,
} from '../../services/firebase';
import './Vol.css';

const DAYS = [
  { key: 'Thu-April-9',  label: 'Thu, Apr 9' },
  { key: 'Fri-April-10', label: 'Fri, Apr 10' },
  { key: 'Sat-April-11', label: 'Sat, Apr 11' },
  { key: 'Sun-April-12', label: 'Sun, Apr 12' },
];

const ERROR_MESSAGES = {
  SHIFT_FULL: 'This shift is now full.',
  SCHEDULING_CLOSED: 'Sign-ups for this shift are closed.',
  ALREADY_SIGNED_UP: 'You are already signed up for this shift.',
  SHIFT_NOT_FOUND: 'This shift has not been set up yet.',
};

function VolSchedule() {
  const { volunteer } = useVolunteerAuth();
  const [selectedDay, setSelectedDay] = useState(DAYS[0].key);
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [portalSettings, setPortalSettings] = useState({ selfSchedulingEnabled: false });
  const [loading, setLoading] = useState(true);
  const [actionState, setActionState] = useState({}); // key → 'loading' | error string
  const unsubRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      const [locs, shifts, settings] = await Promise.all([
        getLocations(), getShiftTimes(), getPortalSettings(),
      ]);
      setLocations(locs.filter(l => l.active).map(l => l.name).sort());
      setShiftTimes(shifts.filter(s => s.active).sort((a, b) => a.order - b.order));
      setPortalSettings(settings);
      setLoading(false);
    };
    init();
    // Live assignment updates — cap and signups update in real time
    unsubRef.current = subscribeToAssignments(data => setAssignments(data));
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, []);

  const isAdmin = localStorage.getItem('adminAuth') === 'true';

  if (loading) {
    return (
      <div className="vol-page">
        <VolNav />
        <div className="vol-content" style={{ color: '#64748b', paddingTop: '40px' }}>Loading…</div>
      </div>
    );
  }

  // Admin can view the schedule but cannot sign up as a volunteer
  const vid = volunteer?.id || null;

  // Fast lookup
  const assignMap = {};
  assignments.forEach(a => { assignMap[`${a.day}_${a.location}_${a.shiftTime}`] = a; });
  const getA = (day, loc, shiftName) => assignMap[`${day}_${loc}_${shiftName}`] || {};

  const getLocalSlots = (a) => {
    const cap = a.localCap || 2;
    const vols = a.volunteers || [];
    const slots = [];
    for (let i = 3; i < 3 + cap; i++) slots.push(vols[i] || null);
    return slots;
  };

  const isSignedUp = (a) => (a.volunteers || []).includes(vid);

  // My shifts across all days for the summary card
  const myShifts = [];
  DAYS.forEach(({ key: day, label }) => {
    const dayAll = shiftTimes.filter(s => !s.days || s.days.includes(day));
    locations.forEach(loc => {
      dayAll.forEach(st => {
        const a = getA(day, loc, st.name);
        if (isSignedUp(a)) myShifts.push({ day, label, loc, shift: st.name });
      });
    });
  });

  const handleAction = async (fn, loc, shiftName) => {
    const key = `${selectedDay}_${loc}_${shiftName}`;
    setActionState(s => ({ ...s, [key]: 'loading' }));
    try {
      await fn(vid, selectedDay, loc, shiftName);
      setActionState(s => ({ ...s, [key]: null }));
    } catch (err) {
      const msg = ERROR_MESSAGES[err.message] || 'Something went wrong. Please try again.';
      setActionState(s => ({ ...s, [key]: msg }));
      setTimeout(() => setActionState(s => ({ ...s, [key]: null })), 5000);
    }
  };

  const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(selectedDay));

  // Locations that have at least one open shift on this day
  const openLocations = locations.filter(loc =>
    dayShifts.some(st => getA(selectedDay, loc, st.name).schedulingOpen)
  );

  return (
    <div className="vol-page">
      <VolNav />
      <div className="vol-content">
        <header className="vol-page-header">
          <h1>Cart Witnessing Shifts</h1>
          <p className="vol-page-subtitle">April 9–12, 2026 · Augusta, Georgia</p>
        </header>

        {/* My shifts summary */}
        {myShifts.length > 0 && (
          <div className="vol-card vol-my-shifts-card">
            <h2 className="vol-card-title">My Shifts ({myShifts.length})</h2>
            <div className="vol-my-shifts-list">
              {myShifts.map((s, i) => (
                <div key={i} className="vol-my-shift-row">
                  <span className="vol-my-shift-day">{s.label}</span>
                  <span className="vol-my-shift-loc">{s.loc}</span>
                  <span className="vol-my-shift-time">{s.shift}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Global closed notice */}
        {!portalSettings.selfSchedulingEnabled && (
          <div className="vol-alert vol-alert-warn">
            Shift sign-ups are not yet open. Check back soon.
          </div>
        )}

        {/* Day tabs */}
        <div className="vol-day-tabs">
          {DAYS.map(d => (
            <button
              key={d.key}
              className={`vol-day-tab${selectedDay === d.key ? ' active' : ''}`}
              onClick={() => setSelectedDay(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Shift cards by location */}
        {openLocations.length === 0 ? (
          portalSettings.selfSchedulingEnabled ? (
            <p style={{ color: '#94a3b8', marginTop: '20px', fontSize: '14px' }}>
              No shifts are open for sign-up on this day yet.
            </p>
          ) : null
        ) : (
          openLocations.map(loc => {
            const openShifts = dayShifts.filter(st => getA(selectedDay, loc, st.name).schedulingOpen);
            return (
              <div key={loc} className="vol-location-section">
                <h3 className="vol-loc-header">{loc}</h3>
                <div className="vol-shift-cards">
                  {openShifts.map(st => {
                    const a = getA(selectedDay, loc, st.name);
                    const slots = getLocalSlots(a);
                    const cap = a.localCap || 2;
                    const filled = slots.filter(Boolean).length;
                    const full = filled >= cap;
                    const signedUp = isSignedUp(a);
                    const key = `${selectedDay}_${loc}_${st.name}`;
                    const state = actionState[key];
                    const busy = state === 'loading';
                    const errMsg = state && state !== 'loading' ? state : null;
                    const schedulingDisabled = !portalSettings.selfSchedulingEnabled;

                    return (
                      <div
                        key={st.id}
                        className={`vol-shift-card${signedUp ? ' vol-shift-signed-up' : ''}${full && !signedUp ? ' vol-shift-full' : ''}`}
                      >
                        <div className="vol-shift-time">{st.name}</div>
                        <div className="vol-shift-spots">
                          <span className={`vol-cap-badge${full ? ' full' : filled > 0 ? ' partial' : ''}`}>
                            {filled}/{cap} spots filled
                          </span>
                        </div>
                        <div className="vol-shift-action">
                          {isAdmin ? (
                            <span className="vol-shift-full-tag" style={{ color: '#64748b', background: '#f1f5f9' }}>
                              Admin view
                            </span>
                          ) : signedUp ? (
                            <button
                              className="vol-btn-cancel"
                              onClick={() => handleAction(selfCancelShift, loc, st.name)}
                              disabled={busy}
                            >
                              {busy ? '…' : '✓ Signed up · Cancel'}
                            </button>
                          ) : full ? (
                            <span className="vol-shift-full-tag">Full</span>
                          ) : (
                            <button
                              className="vol-btn-signup"
                              onClick={() => handleAction(selfSignUpForShift, loc, st.name)}
                              disabled={busy || schedulingDisabled}
                            >
                              {busy ? 'Signing up…' : 'Sign Up'}
                            </button>
                          )}
                        </div>
                        {errMsg && <div className="vol-shift-error">{errMsg}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default VolSchedule;
